import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { useDraft } from '../contexts/DraftContext';
import DEFAULT_SECTIONS from '../config/defaultSections';
import { applySectionMutation, mergeSectionsWithDefaults, normalizeSectionsForSave } from '../utils/pageSections';
import { getAuthorizedContentAdminToken } from '../utils/adminToken';

const STORAGE_KEY = 'poc_page_sections';

/** Convert API camelCase section to hook's snake_case format */
const fromApiFormat = (apiSection, pageName) => ({
  id: apiSection.id ?? apiSection.Id,
  page_name: apiSection.page_name || apiSection.pageName || apiSection.PageName || pageName,
  section_type: apiSection.section_type || apiSection.sectionType || apiSection.SectionType,
  section_order: apiSection.section_order ?? apiSection.sectionOrder ?? apiSection.SectionOrder,
  is_visible: apiSection.is_visible ?? apiSection.isVisible ?? apiSection.IsVisible,
  config: apiSection.config || apiSection.Config || {},
});

/** Convert hook's snake_case section to API camelCase format */
const toApiFormat = (section) => ({
  // Backend DTO Id is `string` and ASP.NET's System.Text.Json is strict about
  // type coercion, so numeric seed IDs from DEFAULT_SECTIONS (1-12) or
  // Date.now() for new sections must be stringified before the PUT.
  id: section.id == null ? '' : String(section.id),
  sectionType: section.section_type,
  sectionOrder: section.section_order,
  isVisible: section.is_visible,
  config: section.config || {},
});

export function usePageSections(pageName) {
  const [sections, setSections] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const allStored = JSON.parse(stored);
        const pageSections = allStored[pageName];
        if (pageSections?.length > 0) {
          const defaults = DEFAULT_SECTIONS[pageName] || [];
          const merged = mergeSectionsWithDefaults(pageSections, defaults);
          return [...merged].sort((a, b) => a.section_order - b.section_order);
        }
      }
    } catch { /* ignore parse errors */ }
    const defaults = DEFAULT_SECTIONS[pageName] || [];
    return [...defaults].sort((a, b) => a.section_order - b.section_order);
  });
  const [loading, setLoading] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const allStored = JSON.parse(stored);
        if (allStored[pageName]?.length > 0) return false;
      }
    } catch {}
    return true;
  });
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  // Guard: skip sectionsDraftSync reload when this hook itself triggered the event
  const selfUpdateRef = useRef(false);
  let draft;
  try { draft = useDraft(); } catch { draft = null; }

  // Keep a ref so loadSections always reads the latest draft (avoids stale closure)
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const loadSections = useCallback(async () => {
    try {
      setLoading(true);

      const defaultSections = DEFAULT_SECTIONS[pageName] || [];

      // 1. Show localStorage cache immediately for instant render
      const stored = localStorage.getItem(STORAGE_KEY);
      const allStored = stored ? JSON.parse(stored) : {};
      let cachedSections = null;

      if (allStored[pageName]?.length > 0) {
        cachedSections = mergeSectionsWithDefaults(allStored[pageName], defaultSections);
        cachedSections.sort((a, b) => a.section_order - b.section_order);
        setSections(cachedSections);
      }

      // 2. Fetch from public API (no auth required)
      let apiSections = null;
      const { tlfId } = getConfig();
      if (tlfId) {
        try {
          const response = await api.get(
            `${apiRoutes.PublicPageSections}?organizationId=${encodeURIComponent(tlfId)}&pageName=${encodeURIComponent(pageName)}`
          );
          const payload = response?.data?.payload;
          if (payload?.sections?.length > 0) {
            apiSections = payload.sections.map(s => fromApiFormat(s, pageName));
          }
        } catch (apiErr) {
          console.debug('Public page sections API unavailable:', apiErr.message);
        }
      }

      if (!mountedRef.current) return;

      // 3. Determine final sections: API > localStorage cache > defaults.
      // When both API and localStorage cache exist, pass the cache as a
      // fallback so user-edited config keys (width/height/slider/etc) are
      // preserved if the API payload comes back with empty config.
      let finalSections;
      if (apiSections?.length > 0) {
        finalSections = mergeSectionsWithDefaults(
          apiSections,
          defaultSections,
          allStored[pageName] || null
        );
      } else if (cachedSections) {
        finalSections = cachedSections;
      } else {
        finalSections = defaultSections;
      }

      finalSections.sort((a, b) => a.section_order - b.section_order);

      // 3b. If DraftContext has sections for this page (admin reorder/visibility
      // changes), prefer them over the published API response. This ensures
      // preview mode and the edit page show the draft state, not the published state.
      const currentDraft = draftRef.current;
      const draftPageSections = currentDraft?.getDraftSections?.(pageName);
      if (draftPageSections?.length > 0) {
        finalSections = mergeSectionsWithDefaults(
          draftPageSections,
          defaultSections,
          finalSections
        );
        finalSections.sort((a, b) => a.section_order - b.section_order);
      }

      // 4. Update localStorage cache
      const freshStored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      finalSections = mergeSectionsWithDefaults(finalSections, defaultSections);
      finalSections.sort((a, b) => a.section_order - b.section_order);

      freshStored[pageName] = finalSections;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(freshStored));

      setSections(finalSections);
      setError(null);
    } catch (err) {
      console.error('Error loading sections:', err);
      if (mountedRef.current) {
        setError(err.message);
        const defaultSections = DEFAULT_SECTIONS[pageName] || [];
        defaultSections.sort((a, b) => a.section_order - b.section_order);
        setSections(defaultSections);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [pageName]);

  useEffect(() => {
    mountedRef.current = true;
    loadSections();
    return () => { mountedRef.current = false; };
  }, [loadSections]);

  // Listen for draft section updates and reload
  useEffect(() => {
    const handleDraftSectionUpdate = (e) => {
      if (e.detail?.pageName === pageName) {
        // Skip reload when this hook instance triggered the event (avoids
        // overwriting the already-correct local state with stale API data)
        if (selfUpdateRef.current) return;
        loadSections();
      }
    };
    window.addEventListener('sectionsDraftSync', handleDraftSectionUpdate);
    return () => window.removeEventListener('sectionsDraftSync', handleDraftSectionUpdate);
  }, [pageName, loadSections]);

  // Listen for draft publish and reload from published API (with delay for replication)
  useEffect(() => {
    const handlePublish = () => {
      // Small delay allows the backend to commit published data before we re-fetch
      setTimeout(() => loadSections(), 600);
    };
    window.addEventListener('draftPublished', handlePublish);
    return () => window.removeEventListener('draftPublished', handlePublish);
  }, [loadSections]);

  // Listen for draft discard and reload from published API
  useEffect(() => {
    const handleDiscard = () => {
      loadSections();
    };
    window.addEventListener('draftDiscarded', handleDiscard);
    return () => window.removeEventListener('draftDiscarded', handleDiscard);
  }, [loadSections]);

  const updateSection = async (sectionId, updates, options = {}) => {
    try {
      // Read current sections from localStorage (live cache) as base
      const stored = localStorage.getItem(STORAGE_KEY);
      const allSections = stored ? JSON.parse(stored) : {};
      const defaults = DEFAULT_SECTIONS[pageName] || [];
      const currentSections = allSections[pageName] || DEFAULT_SECTIONS[pageName] || [];
      const pageSections = applySectionMutation(currentSections, defaults, pageName, sectionId, updates, options);

      // Always persist to localStorage live cache for immediate rendering
      allSections[pageName] = pageSections;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allSections));
      setSections([...pageSections]);

      // skipDraft: auto-registration writes (ensureSectionType) should NOT create a draft
      if (options.skipDraft) return;

      // Write section state to DraftContext instead of pushing to API
      if (draft) {
        selfUpdateRef.current = true;
        draft.updateDraftSections(pageName, pageSections);
        // Clear after microtask so the synchronous event handler sees the flag
        Promise.resolve().then(() => { selfUpdateRef.current = false; });
        return; // Don't push to API — draft mode defers until Publish
      }

      // Fallback: if no DraftContext, push directly to API (legacy behavior)
      const adminToken = getAuthorizedContentAdminToken(getConfig().tlfId);
      if (adminToken) {
        try {
          await api.put(apiRoutes.PageSections, {
            pageName,
            sections: normalizeSectionsForSave(pageSections, defaults).map(toApiFormat),
          });
          console.log(`[PageSections] Synced ${pageSections.length} sections to server for "${pageName}"`);
          setError(null);
        } catch (apiErr) {
          console.error('Failed to save sections to API:', apiErr);
          const status = apiErr?.response?.status;
          const reason =
            status === 401 || status === 403
              ? 'Not synced — sign in via /admin to sync across browsers.'
              : `Not synced — server error (${status || 'network'}).`;
          setError(reason);
        }
      } else {
        const reason = 'Not synced — no admin token. Sign in via /admin first.';
        console.warn(`[PageSections] ${reason}`);
        setError(reason);
      }
    } catch (err) {
      console.error('Error updating section:', err);
    }
  };

  // Track already-registered types to avoid duplicate creation during
  // the same render cycle (multiple SectionItems mount simultaneously).
  const registeredRef = useRef(new Set());

  // Sync the ref with current section types so re-renders stay consistent.
  useMemo(() => {
    registeredRef.current = new Set(sections.map(s => s.section_type));
  }, [sections]);

  /**
   * Auto-register a section type if it doesn't exist yet.
   * Called by EditModeSectionLabel on mount so every rendered section gets admin controls.
   * Skipped while sections are still loading to avoid creating duplicates
   * before the real data arrives from localStorage/API.
   */
  const ensureSectionType = useCallback((sectionType) => {
    if (!sectionType || loading) return;
    if (registeredRef.current.has(sectionType)) return;
    // Mark as pending immediately to deduplicate rapid calls
    registeredRef.current.add(sectionType);
    // skipDraft: auto-registration is a cache convenience, not a user action
    updateSection(null, {
      section_type: sectionType,
      section_order: registeredRef.current.size,
      is_visible: true,
      config: {},
    }, { skipDraft: true });
  }, [loading, updateSection]);

  return { sections, loading, error, updateSection, ensureSectionType, refetch: loadSections };
}
